import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import { PRIMARY_BUTTON } from "@/constants";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import SkeletonType1 from "@/app/components/ui/Skeletons/Type1";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import PaginationComponent from "@/app/components/ui/Pagination";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { IRotaState } from "@/app_redux/reducers/slices/auth/rota_slice";
import { useConfigurationFilterStaffListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationFilterStaffListAPI";
import { useRotaShiftCategoryAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftCategoryAPI";
import { useConfigurationRBACUserRolesAndPermissionListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationRBACUserRolesAndPermissionListAPI";
import { useOrgCitiesListAPI } from "@/app/hooks/api_hooks/org/useOrgCitiesListAPI";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { useForm } from "react-hook-form";
import { TbCircleLetterS } from "react-icons/tb";
import clsx from "clsx";

export interface IStafFiltersFormFields {
  cityId: string;
  roleId: string;
  categoryId: string;
}

const PAGE_SIZE = 10;

const tableHeader = [
  { item: "SN" },
  { item: "Name" },
  { item: "Role" },
  { item: "Category" },
  { item: "Status" },
  { item: "Action" },
];

const ConfigurationStaffSetup = () => {
  let staffcategoriesFetched = false;
  const { NO_APPOINTMENTS } = useImageConstants();
  const isStaffListInProgress = useLoader("isOnboardStaffListInProgress");
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const routeIdRotaConfig = getRouteKey("ROTA", "id");
  const { routeState: routeRotaState } = useRouteInfo(routeIdRotaConfig);
  const { staffList: staffListData } = routeState.state as IConfigurationListState;
  const [searchQuery, setSearchQuery] = useState<string>("");
    const [currentPage, setCurrentPage] = useState<number>(1);
  const { category, cities: cityList } = routeRotaState.state as IRotaState;
  const fetchStafList = useConfigurationFilterStaffListAPI();
  const fetchShiftCategoryAPI = useRotaShiftCategoryAPI();
  const { execute: fetchStaffRoles, rolesAndPermissionList } = useConfigurationRBACUserRolesAndPermissionListAPI();
  const fetchCities = useOrgCitiesListAPI("ROTA");
  const staffResults = staffListData?.results || [];
  const totalRecords = staffListData?.total_records || 0;
  const cityOrgItem = useSelectMenuReducer(cityList, "name", "id", {
    withAllSelectOption: true,
  });
  const categoryListItem = useSelectMenuReducer(category, "staff_category_name", "staff_category_id", {
    withAllSelectOption: true,
  });

  const roleListItems = useSelectMenuReducer(rolesAndPermissionList, "role_name", "role_id", {
    withAllSelectOption: true,
  });

  const formHook = useForm<IStafFiltersFormFields>({
    mode: "onChange",
    defaultValues: {
      cityId: "1",
      categoryId: "-1",
      roleId: "-1",
    },
  });

  useEffect(() => {
    if (!staffcategoriesFetched) {
      staffcategoriesFetched = true;
    }
  }, []);

  useEffect(() => {
    fetchCities({
      withClinics: false,
      staffCategoryId: "2",
      withClinicProviders: false,
      clinicOptions: {
        selectFirst: true,
      },
    }).then(() => {
      fetchShiftCategoryAPI().then(() => {
        fetchStaffRoles();
        fetchStafList(
          formHook.getValues("cityId"),
          formHook.getValues("categoryId"),
          formHook.getValues("roleId"),
          1,
          PAGE_SIZE,
          searchQuery,
        );
      });
    });
  }, []);

  const validateConditionalFormFields = (data: IStafFiltersFormFields) => {
    let isValid = false;
    if (data?.cityId !== "" || data?.categoryId !== "" || data?.roleId !== "") {
      isValid = true;
    }
    return isValid;
  };

  const Submit = (data: IStafFiltersFormFields) => {
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    if (data && isFormSubmissionValid) {
      fetchStafList(data.cityId, data.categoryId, data.roleId, 1, PAGE_SIZE, searchQuery);
    }
  };

  return (
    <div>
      <div className="px-4 sm:px-6 lg:px-8 bg-white py-6 rounded-md">
        <form className="w-full" onSubmit={formHook.handleSubmit(Submit)}>
          <div className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-[10px] items-end">
            <div>
              <label className="block mb-1.5 text-[10px] sm:text-xs">City</label>
              <SearchableSelectMenu
                errorMessages={[{ message: "City is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("cityId", item.id);
                  }
                }}
                fieldError={formHook.formState.errors.cityId}
                register={formHook.register("cityId", { required: true })}
                selectItems={cityOrgItem}
                placeholder="Select City"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={cityOrgItem?.filter((oc) => oc.id === formHook.watch("cityId"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <div>
              <label className="block mb-1.5 text-[10px] sm:text-xs">Staff Category</label>
              <SearchableSelectMenu
                errorMessages={[{ message: "Category is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("categoryId", item.id);
                  }
                }}
                fieldError={formHook.formState.errors.categoryId}
                register={formHook.register("categoryId", { required: true })}
                selectItems={categoryListItem}
                placeholder="Select Category"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={categoryListItem?.filter((oc) => oc.id === formHook.watch("categoryId"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <div>
              <label className="block mb-1.5 text-[10px] sm:text-xs">Staff Role</label>
              <SearchableSelectMenu
                errorMessages={[{ message: "Role is required", type: "required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("roleId", item.id);
                  }
                }}
                fieldError={formHook.formState.errors.roleId}
                register={formHook.register("roleId", { required: true })}
                selectItems={roleListItems}
                placeholder="Select Role"
                showTooltips={true}
                showTypedErrors={true}
                showDropdownIcon={true}
                defaultSelected={roleListItems?.filter((oc) => oc.id === formHook.watch("roleId"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
                containerClassName="w-full"
              />
            </div>
            <button type="submit" className={clsx(PRIMARY_BUTTON, "w-full sm:w-auto sm:justify-self-start")}>
              Apply Filters
            </button>
          </div>
        </form>
      </div>
      <div className="px-4 sm:px-6 lg:px-8 bg-white py-3 rounded-md">
        <div className="flex flex-row items-center justify-between gap-3 my-4 sm:my-0">
          <h1 className="text-sm sm:text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">Staff List</h1>
          <div className="shrink-0">
            <SolidButton asLink href={getRouteKey("STAFF_SETUP_ONBOARD", "url")} size="XL">
              Onboard New Staff
            </SolidButton>
          </div>
        </div>
        <div className="hidden sm:flex">
          <InputWithAddOn
            onEnterKeyPress={(query: string) => {
              setSearchQuery(query);
              setCurrentPage(1);
              fetchStafList(
                formHook.getValues("cityId"),
                formHook.getValues("categoryId"),
                formHook.getValues("roleId"),
                1,
                PAGE_SIZE,
                query,
              );
            }}
            containerClassName="w-full sm:w-1/2 lg:w-1/3"
            addon={<TbCircleLetterS className="w-4 h-4" />}
            placeholder="Search Staff"
          />
        </div>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          className="p-1 rounded-lg sm:hidden mt-2 w-full border"
          placeholder="Search Staff"
        />
        <div className="mt-6">
          {isStaffListInProgress ? (
            <>
              <SkeletonType1 />
              <SkeletonType1 className="mt-4" />
            </>
          ) : staffResults?.length === 0 ? (
            <NoDataFoundComponent
              imageURL={NO_APPOINTMENTS}
              message="No Staff Data"
              description="No staffs have been found, please onboard a few to see in the list."
            />
          ) : (
            <div className="mt-8 flow-root">
              <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
                <div className="scroll-inner !h-auto max-h-[600px]">
                  <div className="w-full">
                    <div className="inline-block min-w-full align-middle">
                      <div className="bg-white rounded-lg">
                        <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                          <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                            <tr>
                              {tableHeader?.map((key, index) => (
                                <th
                                  key={index}
                                  scope="col"
                                  className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                                >
                                  {key?.item}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="overflow-hidden rounded-xl w-full">
                            {staffResults?.map((staff, idx) => {
                              const isFirst = idx === 0;
                              const isLast = idx === staffResults?.length - 1;
                              const isSingle = staffResults?.length === 1;
                              const isActive = staff?.status == "true";

                              return (
                                <tr
                                  key={staff?.staff_id}
                                  className={clsx(
                                    "group bg-white transition",
                                    isSingle && "rounded-xl",
                                    isFirst && !isSingle && "rounded-t-xl",
                                    isLast && !isSingle && "rounded-b-xl",
                                  )}
                                >
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                      isFirst && !isSingle && "rounded-tl-2xl",
                                      isLast && !isSingle && "rounded-bl-2xl",
                                    )}
                                  >
                                    {(currentPage - 1) * PAGE_SIZE + (idx + 1)}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">{staff?.name}</td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    {staff?.role}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal text-[#6C727F]">
                                    {staff?.category_name}
                                  </td>
                                  <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                    <div
                                      className={clsx(
                                        "inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium",
                                        isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700",
                                      )}
                                    >
                                      <span
                                        className={clsx(
                                          "w-2 h-2 rounded-full",
                                          isActive ? "bg-green-500" : "bg-red-500",
                                        )}
                                      />
                                      {isActive ? "Active" : "InActive"}
                                    </div>
                                  </td>
                                  <td
                                    className={clsx(
                                      "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                      isSingle && "rounded-tr-2xl rounded-br-2xl",
                                      isFirst && !isSingle && "rounded-tr-2xl",
                                      isLast && !isSingle && "rounded-br-2xl",
                                    )}
                                  >
                                    <a
                                      onClick={(event) => {
                                        event.preventDefault();
                                        navigate(
                                          `${getRouteKey("STAFF_SETUP_ONBOARD", "url")}?staff_id=${staff?.staff_id}`,
                                        );
                                      }}
                                      href="#"
                                      className="underline text-xs text-black"
                                    >
                                      Edit<span className="sr-only">, {staff?.staff_id}</span>
                                    </a>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-center items-center p-4 w-full overflow-x-auto">
                <PaginationComponent
                  className="pagination-bar"
                  currentPage={currentPage}
                  pageSize={PAGE_SIZE}
                  totalCount={totalRecords}
                  onPageChange={(pageNumber) => {
                    setCurrentPage(pageNumber);
                    fetchStafList(
                      formHook.getValues("cityId"),
                      formHook.getValues("categoryId"),
                      formHook.getValues("roleId"),
                      pageNumber,
                      PAGE_SIZE,
                      searchQuery,
                    );
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConfigurationStaffSetup;